import { User, UserRole, UserStatus } from '../generated/gql-types';
import { AppContext } from '../type/types';

export const CLIENT_PORTAL_DISABLED_MESSAGE =
  'The client web portal is not available for your organisation. Please use the mobile app to sign in.';

const CLIENT_WEB_PORTAL_PHONE_REGEX = /^(?:\+61|0)4\d{8}$/;

const CLIENT_WEB_ROLES: ReadonlySet<UserRole> = new Set([
  UserRole.ClientAdmin,
  UserRole.ClientUser,
]);

export const PUBLIC_AUTH_PATHS = [
  '/login',
  '/signup',
  '/confirm-signup',
  '/forgot-password',
  '/reset-password',
  '/set-new-password',
] as const;

export const isClientWebRole = (role: UserRole): boolean =>
  CLIENT_WEB_ROLES.has(role);

export const isAuthPortalPath = (path: string): boolean => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return (
    (PUBLIC_AUTH_PATHS as readonly string[]).includes(normalized) ||
    normalized === '/about'
  );
};

export const appContextForRole = (role: UserRole): AppContext => {
  if (isClientWebRole(role)) {
    return AppContext.ClientApp;
  }

  if (role === UserRole.Worker) {
    return AppContext.WorkerApp;
  }

  return AppContext.Portal;
};

/** Post-login: role. Pre-login: shared `/login` uses portal context. */
export const resolveAppContext = (role?: UserRole | null): AppContext => {
  if (role) {
    return appContextForRole(role);
  }

  return AppContext.Portal;
};

export const withPortalPrefix = (path: string): string => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return normalized;
};

type ClientProfileFields = Pick<User, 'firstName' | 'lastName' | 'phoneNumber'>;

export const hasMinimalClientWebPortalProfile = (
  user: ClientProfileFields | null | undefined,
): boolean => {
  const firstName = user?.firstName?.trim() ?? '';
  const lastName = user?.lastName?.trim() ?? '';
  const phoneNumber = user?.phoneNumber?.trim() ?? '';

  if (firstName.length < 2 || firstName.length > 35) {
    return false;
  }

  if (lastName.length < 2 || lastName.length > 35) {
    return false;
  }

  return CLIENT_WEB_PORTAL_PHONE_REGEX.test(phoneNumber);
};

export const needsClientProfileOnboarding = (
  user: Pick<User, 'role' | 'status' | 'firstName' | 'lastName' | 'phoneNumber'> | null | undefined,
): boolean => {
  if (!user?.role || !isClientWebRole(user.role)) {
    return false;
  }

  if (user.status !== UserStatus.Pending) {
    return false;
  }

  return !hasMinimalClientWebPortalProfile(user);
};

export const getClientPostAuthPath = (
  user: Pick<User, 'role' | 'status' | 'firstName' | 'lastName' | 'phoneNumber'> | null | undefined,
): string =>
  needsClientProfileOnboarding(user) ? '/onboarding' : '/jobs';

/** API requests from the client web portal use PORTAL (not CLIENT_APP) so the backend
 *  can distinguish them from the mobile client app. Theme resolution still uses role. */
export const resolveAppContextForApiRequest = (
  role?: UserRole | null,
): AppContext => {
  if (role && isClientWebRole(role)) {
    return AppContext.Portal;
  }

  return resolveAppContext(role);
};
